import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import Plus from "../../assets/svgs/Plus.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import ActionsMenu from "../../components/ActionsMenu";
import BarData from "../../components/Charts/BarData";
import CircleChart from "../../components/Charts/CircleChart";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialClosedFilterData } from "../../utils/initialData";
import LeadDataContainer from "../Leads/components/LeadDataContainer";
import AddClosedModal from "./AddClosedModal";
import ClosedFilter from "./components/ClosedFilter";
import EditClosedModal from "./EditClosedModal";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const Closed = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [expandedRows, setExpandedRows] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [disableDelete, setDisableDelete] = useState(false);
  const [closedDashboard, setClosedDashboard] = useState();
  const queryParams = new URLSearchParams(location.search);

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialClosedFilterData;
  });

  const [filterStatus, setFilterStatus] = useState({
    close_quarter: false,
    close_ytd: false,
    buyer_days_on_market: false,
    seller_days_on_market: false,
    total_volumn_closed: false,
    total_cgi_to_company: false,
    buyer_initial_offer_to_under_contract_conversion: false,
    exclusive_to_under_contract_conversion: false,
  });

  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  // URL sync with debounced search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  // ==================== Existing Logic ====================
  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }
    const { created_at, created_start_date, created_end_date, property_name, min_contract_price, max_contract_price, min_contract_cap_rate, max_contract_cap_rate,
      close_quarter, close_ytd, buyer_days_on_market, seller_days_on_market, total_volumn_closed, total_cgi_to_company, buyer_initial_offer_to_under_contract_conversion, exclusive_to_under_contract_conversion,
      gci_closing_quarter1, gci_closing_quarter2, gci_closing_quarter3, gci_closing_quarter4
    } = filters;

    if (created_at) {
      params += `&created_at=${encodeURIComponent(created_at)}`;
    }
    if (created_start_date) {
      params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    }
    if (created_end_date) {
      params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    }
    if (property_name) {
      params += `&property_name=${encodeURIComponent(property_name)}`;
    }
    if (min_contract_price) {
      params += `&min_contract_price=${encodeURIComponent(min_contract_price)}`;
    }
    if (max_contract_price) {
      params += `&max_contract_price=${encodeURIComponent(max_contract_price)}`;
    }
    if (min_contract_cap_rate) {
      params += `&min_contract_cap_rate=${encodeURIComponent(min_contract_cap_rate)}`;
    }
    if (max_contract_cap_rate) {
      params += `&max_contract_cap_rate=${encodeURIComponent(max_contract_cap_rate)}`;
    }
    if (close_quarter) {
      params += `&close_quarter=true`;
    }
    if (close_ytd) {
      params += `&close_ytd=true`;
    }
    if (buyer_days_on_market) {
      params += `&buyer_days_on_market=true`;
    }
    if (seller_days_on_market) {
      params += `&seller_days_on_market=true`;
    }
    if (total_volumn_closed) {
      params += `&total_volumn_closed=true`;
    }
    if (total_cgi_to_company) {
      params += `&total_cgi_to_company=true`;
    }
    if (buyer_initial_offer_to_under_contract_conversion) {
      params += `&buyer_initial_offer_to_under_contract_conversion=true`;
    }
    if (exclusive_to_under_contract_conversion) {
      params += `&exclusive_to_under_contract_conversion=true`;
    }
    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id" && sortBy !== undefined) {
      params += `&sort_by=${sortBy}`;
    }
    if (gci_closing_quarter1) {
      params += `&gci_closing_quarter1=true`;
    }
    if (gci_closing_quarter2) {
      params += `&gci_closing_quarter2=true`;
    }
    if (gci_closing_quarter3) {
      params += `&gci_closing_quarter3=true`;
    }
    if (gci_closing_quarter4) {
      params += `&gci_closing_quarter4=true`;
    }

    return params;
  };

  const fetchData = (filters) => {
    const queryParams = buildQueryParams({
      ...filters,
      close_quarter: filterStatus.close_quarter,
      close_ytd: filterStatus.close_ytd,
      buyer_days_on_market: filterStatus.buyer_days_on_market,
      seller_days_on_market: filterStatus.seller_days_on_market,
      total_volumn_closed: filterStatus.total_volumn_closed,
      total_cgi_to_company: filterStatus.total_cgi_to_company,
      buyer_initial_offer_to_under_contract_conversion: filterStatus.buyer_initial_offer_to_under_contract_conversion,
      exclusive_to_under_contract_conversion: filterStatus.exclusive_to_under_contract_conversion,
      gci_closing_quarter1: filterStatus.gci_closing_quarter1,
      gci_closing_quarter2: filterStatus.gci_closing_quarter2,
      gci_closing_quarter3: filterStatus.gci_closing_quarter3,
      gci_closing_quarter4: filterStatus.gci_closing_quarter4,
    });
    axios
      .get(`${BASE_URL}/close-listing?active_tab=${activeTab}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.close;
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [sortDirection, sortBy]);

  useEffect(() => {
    onSuccess();
  }, [search, currentPage, itemPerPage, activeTab]);

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchData(filters);
    } else {
      fetchData(filterData);
    }
  };

  const handleDelete = () => {
    setDisableDelete(true);
    axios
      .delete(`${BASE_URL}/delete-close/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        fetchData();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const data = closedDashboard?.pie_graph;

  const BarGraphData = [
    { name: "Q1", count: closedDashboard?.gci_closing_quarter1, key: "gci_closing_quarter1" },
    { name: "Q2", count: closedDashboard?.gci_closing_quarter2, key: "gci_closing_quarter2" },
    { name: "Q3", count: closedDashboard?.gci_closing_quarter3, key: "gci_closing_quarter3" },
    { name: "Q4", count: closedDashboard?.gci_closing_quarter4, key: "gci_closing_quarter4" },
  ];

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const fetchDashboardData = () => {
    setLoading(true);

    axios
      .get(`${BASE_URL}/dashboard-close?active_tab=${activeTab}`, config)
      .then((res) => {
        setClosedDashboard(res?.data?.close);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboardData();
  }, [activeTab]);

  const handleBoxClick = (nameOrKey) => {
    const filterKey = nameOrKey;
    let targetTab = activeTab;
    if (activeTab === 0) {
      targetTab = 1;
    } else if (activeTab === 2) {
      targetTab = 3;
    }

    setActiveTab(targetTab);
    setCurrentPage(1);

    setFilterStatus({
      close_quarter: filterKey === "close_quarter",
      close_ytd: filterKey === "close_ytd",
      buyer_days_on_market: filterKey === "buyer_days_on_market",
      seller_days_on_market: filterKey === "seller_days_on_market",
      total_volumn_closed: filterKey === "total_volumn_closed",
      total_cgi_to_company: filterKey === "total_cgi_to_company",
      buyer_initial_offer_to_under_contract_conversion: filterKey === "buyer_initial_offer_to_under_contract_conversion",
      exclusive_to_under_contract_conversion: filterKey === "exclusive_to_under_contract_conversion",
      gci_closing_quarter1: filterKey === "gci_closing_quarter1",
      gci_closing_quarter2: filterKey === "gci_closing_quarter2",
      gci_closing_quarter3: filterKey === "gci_closing_quarter3",
      gci_closing_quarter4: filterKey === "gci_closing_quarter4",
    });
  };

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">Closed</p>
        <div className="md:flex block gap-6 items-center">
          <div className="flex gap-6 justify-between items-center">
            <ClosedFilter
              filterData={filterData}
              onSetFilterData={(value) => {
                setFilterData(value);
              }}
              from="Closed"
              onCallApiAgain={(filters) => onSuccess(filters)}
            />
            {/* Updated Search Box */}
            <div className="search-box contacts relative">
              <input
                type="text"
                className="body-S"
                placeholder="Search name, address, city, state"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8", marginTop: "0px" }}
              />
            </div>
            <button className="add-contact-button green-bg-H light-L body-S" onClick={() => setShowAddModal(true)}>
              <img className="mr-2" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="task-tabs mx-1 mt-2 flex justify-between body-L dark-M mb-4">
        <div className="flex gap-4">
          {["My Dashboard", "My Closed", "Team Dashboard", "Team Closed"].map((tab, index) => (
            <p
              key={index}
              role="button"
              className={`${activeTab === index ? "head-4 dark-H active" : ""} py-1`}
              onClick={() => {
                setActiveTab(index);
                setCurrentPage(1);
                setFilterStatus({
                  close_quarter: false,
                  close_ytd: false,
                  buyer_days_on_market: false,
                  seller_days_on_market: false,
                  total_volumn_closed: false,
                  total_cgi_to_company: false,
                  buyer_initial_offer_to_under_contract_conversion: false,
                  exclusive_to_under_contract_conversion: false,
                });
              }}
            >
              {tab}
            </p>
          ))}
        </div>
      </div>

      {(activeTab === 1 || activeTab === 3) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : listData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Data Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th>
                      <p>Details</p>
                    </th>
                    <th>
                      <div className="table-header">
                        Closing Date
                        <img
                          role="button"
                          src={sortBy !== "closing_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("closing_date");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th className="green-H ">
                      <div className="table-header">
                        Property Name
                        <img
                          role="button"
                          src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("property_name");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Sale Price
                        <img
                          role="button"
                          src={sortBy !== "sale_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("sale_price");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Sale Cap Rate
                        <img
                          role="button"
                          src={sortBy !== "sale_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("sale_cap_rate");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Gci to company
                        <img
                          role="button"
                          src={sortBy !== "gci_to_company" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("gci_to_company");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">More</div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {listData.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr key={idx} className="dark-H body-N">

                        <td>
                          <img
                            role="button"
                            src={expandedRows[el.id] ? UpArrow : DownArrow}
                            alt="icon"
                            className="sidebar-icons"
                            onClick={() => toggleRowDetails(el.id)}
                          />
                        </td>
                        <td className="text-center">{(el?.close_date)}</td>

                        <td className="head-5 capitalize">
                          <Link
                            to={
                              el?.portfolio === 0
                                ? `/property/${el?.contract?.executed?.property_id}`
                                : el?.contract?.executed?.link?.owner_info_type === "contact"
                                  ? `/contact/${el?.contract?.executed?.link?.owner?.id}`
                                  : `/company/${el?.contract?.executed?.link?.owner?.id}`
                            }
                            onClick={(e) => {
                              e.preventDefault();

                              if (el?.portfolio === 0) {
                                navigate(`/property/${el?.contract?.executed?.property_id}`);
                              } else if (el?.contract?.executed?.link?.owner_info_type === "contact") {
                                navigate(`/contact/${el?.contract?.executed?.link?.owner?.id}`);
                              } else if (el?.link?.contract?.executed?.owner_info_type === "company") {
                                navigate(`/company/${el?.link?.contract?.executed?.owner?.id}`);
                              }
                            }}
                            role="button"
                          >
                            {el?.contract?.executed?.link?.property_name} <br />
                            {el?.contract?.executed?.link?.street_address} <br />
                            {el?.contract?.executed?.link?.city && el?.contract?.executed?.link?.state
                              ? `${el.contract?.executed.link.city}, ${el.contract?.executed.link.state}`
                              : ""}
                          </Link>
                        </td>

                        <td className="text-center">${el?.contract_price_format}</td>
                        <td className="text-center">{el?.contract_cap_rate_format}%</td>
                        <td className="text-center">${el?.contract?.executed?.gross_commission?.gci_to_company}</td>
                        <td className="flex gap-3 items-center w-full justify-center">
                          <ActionsMenu
                            from="Closed"
                            handleEdit={() => {
                              setShowEditModal(true);
                              setSelectedId(el?.id)
                            }}
                            handleDelete={() => {
                              setSelectedId(el?.id);
                              setShowDeleteModal(true);
                            }}
                          />
                        </td>
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">

                                <div className="grid grid-cols-8 gap-6 items-start">

                                  <div>
                                    <p className="dark-H head-4">Acquistion</p>
                                    <Link
                                      to={`/contact/${el?.contract?.executed?.offer?.buyer_contact_id}`}
                                      className="body-N  dark-H "
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/contact/${el?.contract?.executed?.offer?.buyer_contact_id}`);
                                      }}
                                    >
                                      {el?.contract?.executed?.offer?.acquistion_name || " "}
                                    </Link>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4">Disposition</p>
                                    <Link
                                      to={`/contact/${el?.contract?.executed?.offer?.seller_contact_id}`}
                                      className="body-N  dark-H "
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/contact/${el?.contract?.executed?.offer?.seller_contact_id}`);
                                      }}
                                    >
                                      {el?.contract?.executed?.offer?.disposition_name || " "}
                                    </Link>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Initial List Date</p>
                                    <p className="body-N  dark-H">
                                      {moment(el?.inital_date).tz('America/New_York').format("MM/DD/YYYY")}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Contract Date</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract_date || ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.contract?.executed?.offer?.seller_broker?.length > 0 ? (
                                        el.contract.executed.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.contract.executed.offer.seller_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract?.executed?.offer?.seller_bds?.first_name} {el?.contract?.executed?.offer?.seller_bds?.last_name || " "}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.contract?.executed?.offer?.buyer_broker?.length > 0 ? (
                                        el.contract.executed.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.contract.executed.offer.buyer_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract?.executed?.offer?.buyer_bds?.first_name} {el?.contract?.executed?.offer?.buyer_bds?.last_name || " "}
                                    </p>
                                  </div>


                                </div>

                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.contract?.executed?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el?.contract?.executed?.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.contract?.executed?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el?.contract?.executed?.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ContactPagination
            activeTab={activeTab}
            paginationData={paginationData}
            handleNext={handleNext}
            handlePrev={handlePrev}
            onSuccess={onSuccess}
            itemPerPage={itemPerPage}
            handleItemPerPageSelect={(value) => {
              setItemPerPage(value);
              setCurrentPage(1);
            }}
          />
        </div>
      )}

      {(activeTab === 0 || activeTab === 2) && (
        <>
          <div className="flex gap-3">
            <div className="w-[70%]">
              <div className="flex items-center gap-3">
                <LeadDataContainer
                  text="NEW CLOSINGS THIS QUARTER"
                  number={closedDashboard?.close_quarter}
                  onClick={() => handleBoxClick("close_quarter")}
                />
                <LeadDataContainer text="TOTAL TRANSACTIONS CLOSED" number={closedDashboard?.close_ytd} onClick={() => handleBoxClick("close_ytd")} />
                <LeadDataContainer text="BUYER DAYS ON MARKET" number={closedDashboard?.buyer_days_on_market} onClick={() => handleBoxClick("buyer_days_on_market")} />
                <LeadDataContainer text="SELLER DAYS ON MARKET" number={closedDashboard?.seller_days_on_market} onClick={() => handleBoxClick("seller_days_on_market")} />
              </div>
              <div className="flex gap-4 items-center">
                <CircleChart
                  Title="TRANSACTION TYPE"
                  data={data}
                  TotalTitle="Total Closed"
                  Total={closedDashboard?.total_close}
                />


                <BarData
                  className="w-[50%]"
                  Title="GROSS REVENUE GAIN BY QUARTER"
                  BarGraphData={BarGraphData}
                  onBarClick={(key) => handleBoxClick(key)}
                />
              </div>
            </div>

            <div className="w-[30%] ">
              <LeadDataContainer text="TOTAL VOLUME CLOSED" number={`$${(closedDashboard?.total_volumn_closed ?? 0).toLocaleString()}`} onClick={() => handleBoxClick("total_volumn_closed")} />
              <LeadDataContainer text="TOTAL GCI TO COMPANY" number={`$${(closedDashboard?.total_cgi_to_company ?? 0).toLocaleString()}`} onClick={() => handleBoxClick("total_cgi_to_company")} />
              <LeadDataContainer text="YTD BUYER INTIAL OFFER TO UC CONVERSION RATE" number={closedDashboard?.buyer_initial_offer_to_under_contract_conversion} onClick={() => handleBoxClick("buyer_initial_offer_to_under_contract_conversion")} />
              <LeadDataContainer text="YTD EXCLUSIVE TO UC CONVERSION RATE" number={closedDashboard?.exclusive_to_under_contract_conversion} onClick={() => handleBoxClick("exclusive_to_under_contract_conversion")} />
            </div>

          </div>

        </>
      )}

      <AddClosedModal showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />

      <EditClosedModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("");
          onSuccess();
        }}
      />


      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
    </BaseLayout>
  );
};

export default Closed;